import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import DeleteModal from "pages/Calendar/DeleteModal";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { selectUser } from "store/features/auth/authSlice";

import { useGetAllClientLeadsQuery, useUpdateClientLeadMutation } from "store/features/clientLeads/clientLeadsManagement";
import { useGetAllUsersQuery } from "store/features/users/userManagementApi";

const AllLeadsSummary = () => {
  const user = useSelector(selectUser);

  const { data: clientLeads, isLoading, refetch } = useGetAllClientLeadsQuery();
  const [updateClientLead] = useUpdateClientLeadMutation();
  const { data: allUsers = [] } = useGetAllUsersQuery();

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [dateFilter, setDateFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Get all summaries from all leads
  const allSummaries = useMemo(() => {
    if (!clientLeads) return [];

    const summaries = [];
    clientLeads.forEach(lead => {
      if (lead.summary && lead.summary.length > 0) {
        lead.summary.forEach(summary => {
          summaries.push({
            ...summary,
            lead_id: lead.id,
            lead_company: lead.company,
            lead_name: lead.name,
            lead_service: lead.service_name
          });
        });
      }
    });
    return summaries;
  }, [clientLeads]);

  // Get marketing users only
  const marketingUsers = useMemo(() => {
    return allUsers.filter(user => user.role == "marketing");
  }, [allUsers]);

  // Filter summaries based on selected filters and user role
  const filteredSummaries = useMemo(() => {
    let filtered = allSummaries;

    // Apply date filter (default to today if no filter)
    if (!dateFilter) {
      const today = new Date().toISOString().split('T')[0];
      filtered = filtered.filter(summary => summary.date == today);
    } else {
      filtered = filtered.filter(summary => summary.date == dateFilter);
    }

    // Apply user filter
    if (userFilter) {
      filtered = filtered.filter(summary => summary.employer_id == userFilter);
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(summary =>
        summary.lead_summary?.toLowerCase().includes(term) ||
        summary.title?.toLowerCase().includes(term) ||
        summary.lead_company?.toLowerCase().includes(term) ||
        summary.employer_name?.toLowerCase().includes(term)
      );
    }

    // Apply role-based filtering - users see only their summaries
    if (user.role !== "super_admin" && user.role !== "admin") {
      filtered = filtered.filter(summary => summary.employer_id == user.id);
    }

    return filtered;
  }, [allSummaries, dateFilter, userFilter, searchTerm, user]);

  const showDeleteModal = (summary) => {
    setSelectedSummary(summary);
    setDeleteModal(true);
  };

  const handleDeleteSummary = async () => {
    if (!selectedSummary) return;

    try {
      // Find the lead that contains this summary
      const lead = clientLeads.find(lead => lead.id == selectedSummary.lead_id);
      if (lead) {
        // Remove the summary from the array
        const updatedSummaries = lead.summary.filter(
          summary => summary.id !== selectedSummary.id
        );

        // Update the lead
        await updateClientLead({
          id: lead.id,
          formData: {
            summary: updatedSummaries
          }
        }).unwrap();

        toast.success("Summary deleted successfully");
        setDeleteModal(false);
        setSelectedSummary(null);
      }
    } catch (error) {
      toast.error("Failed to delete summary");
      console.error('Delete summary error:', error);
    }
  };

  const columns = useMemo(() => [
    {
      header: "Marketer",
      accessorKey: "employer_name",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div>
          <div className="">{row.original.employer_name}</div>
          <small className="text-muted">{row.original.employer_role}</small>
        </div>
      ),
    },
    {
      header: "Client",
      accessorKey: "lead_name",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div>
          <div className="">{row.original.lead_name}</div>
          {/* <small className="text-muted">
            {row.original.lead_name} - {row.original.lead_service}
          </small> */}

        </div>
      ),
    },
    // {
    //   header: "Title",
    //   accessorKey: "title",
    //   enableColumnFilter: false,
    //   enableSorting: true,
    //   cell: ({ row }) => (
    //     <div className="fw-semibold">
    //       {row.original.title || "No Title"}
    //     </div>
    //   ),
    // },
    {
      header: "Lead Summary",
      accessorKey: "lead_summary",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div>
          <div className="" style={{
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            wordBreak: 'break-word'
          }}>

            {row.original.lead_summary}
          </div>

        </div>
      ),
    },
    {
      header: "Date",
      accessorKey: "date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="">
          {new Date(row.original.date).toLocaleDateString()}
        </div>
      ),
    },
    {
      header: "Hours",
      accessorKey: "hours_spent",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="text-center">
          <span className="badge bg-info fs-6">{row.original.hours_spent}h</span>
        </div>
      ),
    },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="d-flex align-items-center gap-1">

          {(user?.role == "super_admin" || user?.role == "admin") && (
            <button
              className="btn btn-danger btn-sm"
              onClick={() => showDeleteModal(row.original)}
            >
              <i className="fas fa-trash"></i>
            </button>
          )}
        </div>
      ),
    },
  ], [user]);

  if (isLoading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Leads Summary" breadcrumbItem="Daily Leads Summary" />

        {/* Quick Stats */}
        {/* <div className="row mb-4">
          <div className="col-md-3">
            <div className="card bg-primary text-white">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">{filteredSummaries.length}</h4>
                    <span>Total Entries</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-list-alt fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success text-white">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">
                      {filteredSummaries.reduce((sum, summary) => sum + parseFloat(summary.hours_spent || 0), 0).toFixed(1)}
                    </h4>
                    <span>Total Hours</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-clock fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-info text-white">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">
                      {[...new Set(filteredSummaries.map(item => item.employer_name))].length}
                    </h4>
                    <span>Marketing Persons</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-users fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning text-white">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">
                      {[...new Set(filteredSummaries.map(item => item.lead_company))].length}
                    </h4>
                    <span>Companies</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-building fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Filters Section */}
        <div className="row mb-3">
          <div className="col-md-3">
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-control"
              value={dateFilter}
              onChange={e => setDateFilter(e.target.value)}
            />
            {/* {dateFilter && (
              <small
                className="text-primary cursor-pointer"
                onClick={() => setDateFilter("")}
              >
                Show Today's Summary
              </small>
            )} */}
          </div>
          <div className="col-md-3">
            <label className="form-label">Marketing</label>
            <select
              className="form-select"
              value={userFilter}
              onChange={e => setUserFilter(e.target.value)}
            >
              <option value="">All Marketing</option>
              {marketingUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.nickname || user.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Search</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search company, summary or title..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-3 d-flex align-items-end">
            <button
              className="btn btn-light me-2"
              onClick={() => {
                setDateFilter("");
                setUserFilter("");
                setSearchTerm("");
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <TableContainer
          columns={columns}
          data={filteredSummaries}
          isGlobalFilter={false}
          isPagination={true}
          SearchPlaceholder="Search leads summary..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        {filteredSummaries.length == 0 && (
          <div className="text-center py-5">
            <i className="fas fa-list-alt fa-3x text-muted mb-3"></i>
            <h4 className="text-muted">No leads summary found</h4>
            <p className="text-muted">
              {dateFilter ? `No leads summary for selected date` : `No leads summary for today`}
            </p>
          </div>
        )}

        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteSummary}
          loader={false}
          onCloseClick={() => setDeleteModal(false)}
        />
      </div>
    </div>
  );
};

export default AllLeadsSummary;